import React, { useState, useRef } from 'react';
import SEO from '../components/SEO';
import ReCAPTCHA from "react-google-recaptcha";
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { ChevronRight, ChevronLeft, MapPin, Calendar as CalendarIcon, Car, Clock, Loader2 } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useCountries } from '../hooks/useCountries';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BookService = () => {
    const { language } = useLanguage();
    const t = translations[language].bookService;
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { countries } = useCountries();

    // Validation Schema
    const schema = yup.object().shape({
        // emirate: yup.string().required("Emirate is required"), 
        serviceCenter: yup.string().required("Service Center is required"),
        date: yup.date().required("Date is required").typeError("Date is required"),
        time: yup.string().required("Time is required"),
        model: yup.string().required("Model is required"),
        year: yup.string().required("Year is required"),
        mileage: yup.string().required("Mileage is required"),
        licencePlate: yup.string().required("License Plate is required"),
        title: yup.string().required("Title is required"),
        firstName: yup.string().required("First Name is required"),
        lastName: yup.string().required("Last Name is required"),
        email: yup.string().email("Invalid email").required("Email is required"),
        phone: yup.string().matches(/^[0-9]+$/, "Phone must be digits").min(8, "Phone must be at least 8 digits").required("Phone number is required"),
        countryCode: yup.string(),
    });

    const [captchaToken, setCaptchaToken] = useState(null);
    const recaptchaRef = useRef(null);

    const { register, handleSubmit, control, formState: { errors }, reset, watch, setValue, setError } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            emirate: '',
            serviceCenter: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            countryCode: '+968',
            model_id: '',
            year: '',
            mileage: '',
            licencePlate: '',
            date: '',
            time: ''
        }
    });

    // Watch date for calendar navigation
    const selectedDate = watch("date");

    const handleCaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    const onSubmit = async (data) => {
        const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
        if (recaptchaSiteKey && !captchaToken) {
            toast({
                title: language === 'ar' ? "خطأ في التحقق" : "Validation Error",
                description: language === 'ar' ? "يرجى إكمال التحقق من ReCAPTCHA." : "Please complete the ReCAPTCHA verification.",
                variant: "destructive",
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const formData = new FormData();
            // formData.append('emirate', data.emirate);
            formData.append('serviceCenter', data.serviceCenter);

            // Format date to DD-MM-YYYY
            const d = new Date(data.date);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            formData.append('date', `${day}-${month}-${year}`);

            formData.append('time', data.time);
            formData.append('time', data.time);
            formData.append('model_id', data.model); // Sending name as ID for now based on context
            formData.append('year', data.year);
            formData.append('mileage', data.mileage);
            formData.append('licencePlate', data.licencePlate);
            formData.append('title', 'Mr');
            formData.append('firstName', data.firstName);
            formData.append('lastName', data.lastName);
            formData.append('email', data.email);
            formData.append('phone', `${data.countryCode}${data.phone}`);
            formData.append('recaptcha_token', captchaToken);

            const response = await axios.post('https://admin.baicoman.com/api/service-leads', formData); // Using proxy if configured or direct

            if (response.status === 200 || response.status === 201) {
                toast({
                    title: language === 'ar' ? "تم بنجاح" : "Success",
                    description: language === 'ar' ? "تم حجز موعد الخدمة الخاص بك بنجاح." : "Your service appointment has been booked successfully.",
                    variant: "default",
                    className: "bg-green-600 text-white border-none"
                });
                reset();
                if (recaptchaSiteKey && recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }
                setCaptchaToken(null);
            }
        } catch (error) {
            console.error(error);
            if (error.response && error.response.data && error.response.data.errors) {
                const serverErrors = error.response.data.errors;
                Object.keys(serverErrors).forEach((field) => {
                    setError(field, {
                        type: "server",
                        message: serverErrors[field][0],
                    });
                });
                toast({
                    title: language === 'ar' ? "خطأ في التحقق" : "Validation Error",
                    description: language === 'ar' ? "يرجى التحقق من الحقول المطلوبة." : "Please check the form for errors.",
                    variant: "destructive",
                });
            } else {
                toast({
                    title: language === 'ar' ? "خطأ" : "Error",
                    description: language === 'ar' ? "حدث خطأ ما. يرجى المحاولة مرة أخرى." : "Something went wrong. Please try again.",
                    variant: "destructive",
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const nextMonth = () => {
        const current = new Date(selectedDate || new Date());
        const next = new Date(current);
        next.setMonth(current.getMonth() + 1);
        setValue("date", next);
    }

    const prevMonth = () => {
        const current = new Date(selectedDate || new Date());
        const prev = new Date(current);
        prev.setMonth(current.getMonth() - 1);
        setValue("date", prev);
    }

    const emirates = ["Dubai", "Abu Dhabi", "Sharjah", "Ajman", "Umm Al Quwain", "Ras Al Khaimah", "Fujairah"];
    const serviceCenters = ["Azaiba Service Center", "Wadi Kabir Service Center", "Al Amerat Service Center", "Mabella Service Center", "Rusayl – Bodyshop", "Barka Service Center", "Suhar Service Center", "Nizwa Service Center", "Ibri Service Center", "Sur Service Center", "Duqm Service Center", "Maweleh Service Center", "Salalah Service Center"];
    const models = ["BJ30 Hybrid", "BJ40 Honor", "BJ40 PRO", "U5 PLUS"];
    const years = Array.from({ length: 15 }, (_, i) => new Date().getFullYear() - i);

    return (
        <div className="min-h-screen bg-white font-sans text-gray-900 selection:bg-red-100">
            <SEO
                title="Book Car Service | BAIC Oman Service Centers"
                description="Schedule your car maintenance or repair at BAIC Service Centers in Oman. Book online now."
                canonicalUrl="https://baicoman.com/book-service"
            />
            {/* Split Layout Container */}
            <div className="flex flex-col lg:flex-row min-h-screen">

                {/* Left Side: Visual & Context */}
                <div className="lg:w-1/3 bg-[#f8f9fa] lg:sticky lg:top-0 lg:h-screen lg:overflow-hidden p-6 lg:p-8 flex flex-col justify-between z-10">
                    <div className="mt-12 lg:mt-16">
                        <span className="text-red-600 font-bold tracking-widest uppercase text-xs mb-4 block">{t?.sidebar?.title}</span>
                        <h1 className="text-3xl lg:text-4xl font-extrabold text-gray-900 tracking-tight leading-tight mb-6">
                            {t?.sidebar?.heading}
                        </h1>
                        <p className="text-gray-500 text-base leading-relaxed max-w-md">
                            {t?.sidebar?.desc}
                        </p>
                    </div>

                    <div className="hidden lg:block space-y-8 pr-12">
                        <div className="flex items-start gap-4 group">
                            <div className="w-12 h-12 min-w-12 shrink-0 rounded-full bg-white shadow-sm flex items-center justify-center group-hover:bg-red-600 transition-colors duration-300">
                                <MapPin className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900">
                                    {t.sidebar.steps.location.title}
                                </h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {t.sidebar.steps.location.desc}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 group">
                            <div className="w-12 h-12 min-w-12 shrink-0 rounded-full bg-white shadow-sm flex items-center justify-center group-hover:bg-red-600 transition-colors duration-300">
                                <CalendarIcon className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900">
                                    {t.sidebar.steps.date.title}
                                </h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {t.sidebar.steps.date.desc}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 group">
                            <div className="w-12 h-12 min-w-12 shrink-0 rounded-full bg-white shadow-sm flex items-center justify-center group-hover:bg-red-600 transition-colors duration-300">
                                <Car className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900">
                                    {t.sidebar.steps.vehicle.title}
                                </h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {t.sidebar.steps.vehicle.desc}
                                </p>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Right Side: Form */}
                <div className="lg:w-2/3 bg-white p-6 lg:p-24 pt-24 lg:pt-32">
                    <form onSubmit={handleSubmit(onSubmit)} className="max-w-3xl mx-auto space-y-16 animate-in fade-in slide-in-from-bottom-8 duration-700">

                        {/* Section 1: Location */}
                        <div className="space-y-8 bg-white rounded-3xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center font-bold">1</div>
                                <h2 className="text-2xl font-bold text-gray-900">{t?.form?.location?.title}</h2>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700 ml-1">{t?.form?.location?.emirate}</label>
                                    <div className="relative">
                                        <select {...register("emirate")} className="w-full p-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500/20 text-gray-900 font-medium transition-all hover:bg-gray-100 cursor-pointer appearance-none">
                                            {emirates.map(e => <option key={e} value={e}>{e}</option>)}
                                        </select>
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                            <ChevronRight className="w-5 h-5 text-gray-400 rotate-90" />
                                        </div>
                                    </div>
                                    {errors.emirate && <p className="text-red-500 text-xs ml-1">{errors.emirate.message}</p>}
                                </div> */}
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700 ml-1">{t?.form?.location?.serviceCenter}</label>
                                    <div className="relative">
                                        <select {...register("serviceCenter")} className="w-full p-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500/20 text-gray-900 font-medium transition-all hover:bg-gray-100 cursor-pointer appearance-none">
                                            {serviceCenters.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                            <ChevronRight className="w-5 h-5 text-gray-400 rotate-90" />
                                        </div>
                                    </div>
                                    {errors.serviceCenter && <p className="text-red-500 text-xs ml-1">{errors.serviceCenter.message}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Section 2: Date & Time */}
                        <div className="space-y-8 bg-white rounded-3xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center font-bold">2</div>
                                <h2 className="text-2xl font-bold text-gray-900">{t?.form?.date?.title}</h2>
                            </div>

                            <div className="flex flex-col lg:flex-row gap-12">
                                <div className="flex-1">
                                    <div className="bg-[#1a1a1a] text-white p-8 rounded-3xl shadow-xl">
                                        <div className="flex justify-between items-center mb-6">
                                            <button type="button" onClick={prevMonth} className="hover:text-red-500 transition-colors p-2 hover:bg-white/10 rounded-full"><ChevronLeft className="h-5 w-5" /></button>
                                            <span className="font-bold text-lg tracking-wide">
                                                {selectedDate instanceof Date && !isNaN(selectedDate) ? selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' }) : new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
                                            </span>
                                            <button type="button" onClick={nextMonth} className="hover:text-red-500 transition-colors p-2 hover:bg-white/10 rounded-full"><ChevronRight className="h-5 w-5" /></button>
                                        </div>
                                        <Controller
                                            control={control}
                                            name="date"
                                            render={({ field }) => (
                                                <Calendar
                                                    mode="single"
                                                    selected={field.value}
                                                    onSelect={field.onChange}
                                                    className="rounded-md border-none w-full text-white"
                                                    classNames={{
                                                        day_selected: "bg-red-600 text-white hover:bg-red-600 focus:bg-red-600 shadow-lg shadow-red-600/30",
                                                        day_today: "bg-white/10 text-white font-bold",
                                                        day: "text-white/80 hover:bg-white/20 hover:text-white rounded-full w-10 h-10 transition-all duration-200",
                                                        head_cell: "text-gray-500 w-10 font-medium text-xs uppercase tracking-wider mb-2",
                                                        caption: "hidden",
                                                        table: "w-full border-collapse space-y-1",
                                                        nav_button: "hidden"
                                                    }}
                                                />
                                            )}
                                        />
                                        {errors.date && <p className="text-red-500 text-xs mt-2">{errors.date.message}</p>}
                                    </div>
                                </div>

                                <div className="lg:w-1/3 space-y-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 ml-1 flex items-center gap-2"><Clock className="w-4 h-4" /> {t?.form?.date?.time}</label>
                                        <input
                                            type="time"
                                            {...register("time")}
                                            className="w-full p-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500/20 text-gray-900 font-medium transition-all hover:bg-gray-100"
                                        />
                                        {errors.time && <p className="text-red-500 text-xs ml-1">{errors.time.message}</p>}
                                    </div>
                                    <div className="bg-blue-50 p-6 rounded-2xl text-blue-900 text-sm leading-relaxed">
                                        <p><strong>Note:</strong> {t?.form?.date?.note}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Section 3: Details */}
                        <div className="space-y-8 bg-white rounded-3xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center font-bold">3</div>
                                <h2 className="text-2xl font-bold text-gray-900">{t?.form?.details?.title}</h2>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* Car Details */}
                                <div className="space-y-6">
                                    <h3 className="font-bold text-gray-400 uppercase tracking-widest text-xs mb-4 border-b pb-2">{t?.form?.details?.vehicleInfo}</h3>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 ml-1">{t?.form?.details?.model}</label>
                                        <select {...register("model")} className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100 appearance-none">
                                            <option value="">{t?.placeholders?.selectModel}</option>
                                            {models.map(m => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                        {errors.model && <p className="text-red-500 text-xs ml-1">{errors.model.message}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 ml-1">{t?.form?.details?.year}</label>
                                        <select {...register("year")} className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100 appearance-none">
                                            <option value="">{t?.placeholders?.selectYear}</option>
                                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                                        </select>
                                        {errors.year && <p className="text-red-500 text-xs ml-1">{errors.year.message}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 ml-1">{t?.form?.details?.mileage}</label>
                                        <input {...register("mileage")} type="text" placeholder={t?.placeholders?.mileage} className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100 placeholder-gray-400" />
                                        {errors.mileage && <p className="text-red-500 text-xs ml-1">{errors.mileage.message}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 ml-1">{t?.form?.details?.licensePlate}</label>
                                        <input {...register("licencePlate")} type="text" placeholder={t?.placeholders?.license} className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100 placeholder-gray-400" />
                                        {errors.licencePlate && <p className="text-red-500 text-xs ml-1">{errors.licencePlate.message}</p>}
                                    </div>
                                </div>

                                {/* Personal Details */}
                                <div className="space-y-6">
                                    <h3 className="font-bold text-gray-400 uppercase tracking-widest text-xs mb-4 border-b pb-2">{t?.form?.details?.contactInfo}</h3>

                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="col-span-1 space-y-2">
                                            <label className="text-sm font-semibold text-gray-700 ml-1">{t?.form?.details?.title}</label>
                                            <select {...register("title")} className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100 appearance-none">
                                                <option value="Mr">Mr.</option>
                                                <option value="Ms">Ms.</option>
                                                <option value="Mrs">Mrs.</option>
                                            </select>
                                            {errors.title && <p className="text-red-500 text-xs ml-1">{errors.title.message}</p>}
                                        </div>
                                        <div className="col-span-2 space-y-2">
                                            <label className="text-sm font-semibold text-gray-700 ml-1">{t?.form?.details?.firstName}</label>
                                            <input {...register("firstName")} type="text" className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100" />
                                            {errors.firstName && <p className="text-red-500 text-xs ml-1">{errors.firstName.message}</p>}
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 ml-1">{t?.form?.details?.lastName}</label>
                                        <input {...register("lastName")} type="text" className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100" />
                                        {errors.lastName && <p className="text-red-500 text-xs ml-1">{errors.lastName.message}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 ml-1">{t?.form?.details?.email}</label>
                                        <input {...register("email")} type="email" className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100" />
                                        {errors.email && <p className="text-red-500 text-xs ml-1">{errors.email.message}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 ml-1">{t?.form?.details?.phone}</label>
                                        <div className="flex gap-4">
                                            <div className="w-1/3">
                                                <Controller
                                                    control={control}
                                                    name="countryCode"
                                                    render={({ field }) => (
                                                        <Select onValueChange={field.onChange} value={field.value}>
                                                            <SelectTrigger className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100 h-[56px]">
                                                                <SelectValue placeholder="+968" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {countries.map((country, index) => (
                                                                    <SelectItem key={index} value={country.phone_prefix}>{country.phone_prefix} ({country.code})</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    )}
                                                />
                                            </div>
                                            <div className="w-2/3">
                                                <input {...register("phone")} type="tel" className="w-full p-4 bg-gray-50 border-none rounded-2xl text-gray-900 font-medium transition-all hover:bg-gray-100 h-[56px]" />
                                            </div>
                                        </div>
                                        {errors.phone && <p className="text-red-500 text-xs ml-1">{errors.phone.message}</p>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Submit Section */}
                        <div className="pt-8 flex flex-col items-center space-y-8">
                            <div className="flex justify-center">
                                {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                                    <div className="w-full flex flex-col items-center gap-2">
                                        <ReCAPTCHA
                                            ref={recaptchaRef}
                                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                            onChange={handleCaptchaChange}
                                        />
                                        {errors.recaptcha_token && <p className="text-red-500 text-xs">{errors.recaptcha_token.message}</p>}
                                    </div>
                                )}
                            </div>

                            <Button disabled={isSubmitting} type="submit" size="lg" className="w-full md:w-auto min-w-[300px] rounded-full px-12 py-8 bg-black text-white hover:bg-red-600 border-none transition-all duration-300 text-lg font-bold shadow-xl hover:shadow-red-600/30 hover:-translate-y-1">
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {language === "ar" ? "جار المعالجة..." : "Processing..."}
                                    </>
                                ) : (
                                    t?.form?.submit
                                )}
                            </Button>

                            <p className="text-center text-gray-400 text-sm">
                                {t?.form?.agreement} <a href="/privacy-policy" className="underline hover:text-gray-900">Privacy Policy</a> and <a href="/terms-and-conditions" className="underline hover:text-gray-900">Terms of Service</a>.
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default BookService;
